/**
 * Invitation Service
 * API calls for user invitation operations
 */

import apiClient from './api.client';
import type { ApiResponse } from '@/types/api.types';

export interface Invitation {
  id: string;
  email: string;
  role: string;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
  invitedBy: {
    id: string;
    displayName: string | null;
    email: string;
  };
}

export interface InvitationStats {
  pending: number;
  accepted: number;
  expired: number;
}

export interface CreateInvitationInput {
  email: string;
  role: 'admin' | 'employee';
}

export interface AcceptInvitationInput {
  firstName: string;
  lastName: string;
  displayName?: string;
  password: string;
}

export interface ValidateTokenResponse {
  email: string;
  role: string;
}

export interface CreatedUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string | null;
  role: string;
}

/**
 * Admin: Create and send a new invitation
 */
export const createInvitation = async (input: CreateInvitationInput): Promise<Invitation> => {
  const response = await apiClient.post<ApiResponse<{ invitation: Invitation }>>(
    '/admin/invitations',
    input
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to create invitation');
  }

  return response.data.data.invitation;
};

/**
 * Admin: Get all pending invitations
 */
export const getPendingInvitations = async (): Promise<Invitation[]> => {
  const response = await apiClient.get<ApiResponse<{ invitations: Invitation[] }>>(
    '/admin/invitations'
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch invitations');
  }

  return response.data.data.invitations;
};

/**
 * Admin: Get invitation statistics
 */
export const getInvitationStats = async (): Promise<InvitationStats> => {
  const response = await apiClient.get<ApiResponse<{ stats: InvitationStats }>>(
    '/admin/invitations/stats'
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch invitation stats');
  }

  return response.data.data.stats;
};

/**
 * Admin: Resend invitation email
 */
export const resendInvitation = async (invitationId: string): Promise<void> => {
  const response = await apiClient.post<ApiResponse<null>>(
    `/admin/invitations/${invitationId}/resend`
  );

  if (response.data.status !== 'success') {
    throw new Error(response.data.error || 'Failed to resend invitation');
  }
};

/**
 * Admin: Revoke/cancel an invitation
 */
export const revokeInvitation = async (invitationId: string): Promise<void> => {
  const response = await apiClient.delete<ApiResponse<null>>(
    `/admin/invitations/${invitationId}`
  );

  if (response.data.status !== 'success') {
    throw new Error(response.data.error || 'Failed to revoke invitation');
  }
};

/**
 * Admin: Test SMTP connection
 */
export const testSmtpConnection = async (): Promise<boolean> => {
  const response = await apiClient.get<ApiResponse<null>>('/admin/invitations/test-smtp');
  return response.data.status === 'success';
};

/**
 * Public: Validate an invitation token
 */
export const validateToken = async (token: string): Promise<ValidateTokenResponse> => {
  const response = await apiClient.get<ApiResponse<ValidateTokenResponse>>(
    `/invitations/${token}/validate`
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Invalid invitation token');
  }

  return response.data.data;
};

/**
 * Public: Accept invitation and create user account
 */
export const acceptInvitation = async (
  token: string,
  input: AcceptInvitationInput
): Promise<CreatedUser> => {
  const response = await apiClient.post<ApiResponse<{ user: CreatedUser }>>(
    `/invitations/${token}/accept`,
    input
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to accept invitation');
  }

  return response.data.data.user;
};

/**
 * Admin: Get SMTP configuration status
 */
export interface SMTPStatus {
  configured: boolean;
  host: string | null;
  port: number | null;
  secure: boolean | null;
  user: string | null;
  from: string | null;
  fromName: string | null;
  hasPassword: boolean;
}

export const getSMTPStatus = async (): Promise<SMTPStatus> => {
  const response = await apiClient.get<ApiResponse<{ smtp: SMTPStatus }>>(
    '/admin/invitations/smtp-status'
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch SMTP status');
  }

  return response.data.data.smtp;
};
